/**
 * BadgeShowcase Component
 *
 * Displays a grid of user's top badges for public profiles.
 * Shows badges sorted by rarity with visual indicators.
 */

import PropTypes from 'prop-types'

const RARITY_COLORS = {
  COMMON: {
    bg: 'from-gray-400 to-gray-500',
    border: 'border-gray-400',
    text: 'text-gray-600 dark:text-gray-400'
  },
  UNCOMMON: {
    bg: 'from-green-400 to-green-600',
    border: 'border-green-500',
    text: 'text-green-600 dark:text-green-400'
  },
  RARE: {
    bg: 'from-blue-400 to-blue-600',
    border: 'border-blue-500',
    text: 'text-blue-600 dark:text-blue-400'
  },
  EPIC: {
    bg: 'from-purple-400 to-purple-600',
    border: 'border-purple-500',
    text: 'text-purple-600 dark:text-purple-400'
  },
  LEGENDARY: {
    bg: 'from-yellow-400 via-orange-500 to-red-500',
    border: 'border-yellow-500',
    text: 'text-yellow-600 dark:text-yellow-400'
  }
}

const BadgeItem = ({ badge }) => {
  const rarityStyle = RARITY_COLORS[badge.rarity] || RARITY_COLORS.COMMON

  return (
    <div
      className={`relative group rounded-lg p-2 bg-gradient-to-br ${rarityStyle.bg} transition-transform hover:scale-105`}
      title={`${badge.name} (${badge.rarity?.toLowerCase()})`}
    >
      {/* Badge Icon or Placeholder */}
      <div className="w-12 h-12 mx-auto flex items-center justify-center">
        {badge.iconPath ? (
          <img
            src={badge.iconPath}
            alt={badge.name}
            className="w-10 h-10 object-contain"
            onError={(e) => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'flex'
            }}
          />
        ) : null}
        <div
          className={`w-10 h-10 rounded-full bg-white/20 items-center justify-center ${badge.iconPath ? 'hidden' : 'flex'}`}
        >
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </div>
      </div>

      {/* Badge Name Tooltip on Hover */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
        {badge.name}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </div>
    </div>
  )
}

BadgeItem.propTypes = {
  badge: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string.isRequired,
    rarity: PropTypes.oneOf(['COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY']),
    iconPath: PropTypes.string
  }).isRequired
}

const BadgeShowcase = ({ badges = [], totalBadges = 0, maxDisplay = 6, className = '' }) => {
  // Show empty state if no badges
  if (!badges || badges.length === 0) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Achievements</h3>
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <svg
            className="w-10 h-10 mx-auto text-gray-400 dark:text-gray-500 mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
          <p className="text-sm">No badges earned yet</p>
        </div>
      </div>
    )
  }

  const displayedBadges = badges.slice(0, maxDisplay)
  const remainingCount = totalBadges - displayedBadges.length

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 ${className}`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Achievements</h3>
        {totalBadges > 0 && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {totalBadges} badge{totalBadges !== 1 ? 's' : ''} earned
          </span>
        )}
      </div>

      {/* Badge Grid */}
      <div className="flex flex-wrap gap-3 justify-center">
        {displayedBadges.map((badge, index) => (
          <BadgeItem key={badge.code || index} badge={badge} />
        ))}

        {/* Show "+X more" indicator if there are more badges */}
        {remainingCount > 0 && (
          <div className="flex items-center justify-center w-12 h-16 text-gray-500 dark:text-gray-400">
            <span className="text-sm font-medium">+{remainingCount}</span>
          </div>
        )}
      </div>

      {/* Rarity Legend */}
      <div className="mt-4 pt-3 border-t dark:border-gray-700">
        <div className="flex flex-wrap justify-center gap-3 text-xs">
          {['LEGENDARY', 'EPIC', 'RARE', 'UNCOMMON', 'COMMON'].map((rarity) => {
            const style = RARITY_COLORS[rarity]
            const hasBadgeOfRarity = badges.some((b) => b.rarity === rarity)
            if (!hasBadgeOfRarity) return null
            return (
              <div key={rarity} className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${style.bg}`} />
                <span className={`capitalize ${style.text}`}>{rarity.toLowerCase()}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

BadgeShowcase.propTypes = {
  /** Array of badge objects to display */
  badges: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string.isRequired,
      rarity: PropTypes.oneOf(['COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY']),
      iconPath: PropTypes.string
    })
  ),
  /** Total number of badges the user has earned */
  totalBadges: PropTypes.number,
  /** Maximum number of badges to display */
  maxDisplay: PropTypes.number,
  /** Additional CSS classes */
  className: PropTypes.string
}

export default BadgeShowcase
